/**
 * AuthStore - API helper functions
 *
 * Standalone helpers for JWT decode, token validation, profile fetching,
 * and token refresh. Used by the auth store actions.
 */

import type { User, TokenPair } from '../types'

const API_BASE = `${import.meta.env.VITE_API_URL || 'https://quotech-backend.quotech.ai'}/api`

/** Decode JWT payload without verification */
export function decodeJWT(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null
    const decoded = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))
    return JSON.parse(decoded)
  } catch {
    return null
  }
}

/** Check if a JWT is expired with optional buffer */
export function isTokenExpired(token: string, bufferSeconds = 5): boolean {
  const payload = decodeJWT(token)
  if (!payload || typeof payload.exp !== 'number') return true
  return Date.now() >= (payload.exp * 1000) - (bufferSeconds * 1000)
}

/** Check if token will expire within 5 minutes */
export function isTokenExpiringSoon(token: string): boolean {
  const payload = decodeJWT(token)
  if (!payload || typeof payload.exp !== 'number') return true
  return Date.now() >= (payload.exp * 1000) - (5 * 60 * 1000)
}

/** Fetch with abort-controller timeout */
export async function fetchWithTimeout(
  url: string,
  options: RequestInit,
  timeoutMs = 8000,
): Promise<Response> {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs)
  try {
    const response = await fetch(url, { ...options, signal: controller.signal })
    clearTimeout(timeoutId)
    return response
  } catch (error) {
    clearTimeout(timeoutId)
    throw error
  }
}

/**
 * Fetch user profile from backend /auth/profile/.
 *
 * `orgId` names the org the shell is currently in. The authority/display
 * address is membership-scoped, so without it the backend answers for the
 * user's remembered DEFAULT org and a multi-org user sees the wrong address
 * after switching. `X-Organisation-ID` is the canonical org signal (ADR-003);
 * the response echoes `authority_organisation_id` so the answer can be cached
 * against the org it actually describes.
 */
export async function fetchUserProfile(
  token: string,
  orgId?: string | null,
): Promise<User | null> {
  const url = `${API_BASE}/auth/profile/`
  console.log('[auth] fetchUserProfile: calling', url, 'org', orgId ?? '(default)')

  try {
    const response = await fetchWithTimeout(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...(orgId ? { 'X-Organisation-ID': String(orgId) } : {}),
      },
    }, 8000)

    console.log('[auth] fetchUserProfile: response status', response.status)
    if (!response.ok) {
      const text = await response.text()
      console.error('[auth] fetchUserProfile: not ok', response.status, text)
      return null
    }

    const data = await response.json()
    console.log('[auth] fetchUserProfile: success, got data')

    let derivedRole = data.role
    if (!derivedRole) {
      if (data.is_superuser === true) derivedRole = 'superadmin'
      else if (data.admin === true || data.is_admin === true || data.is_staff === true) derivedRole = 'admin'
      else derivedRole = 'member'
    }

    return {
      id: data.id || data.user_id || '',
      email: data.email || '',
      login_email: data.login_email || data.email || '',
      authority_email: data.authority_email || '',
      display_email: data.display_email || data.authority_email || data.email || '',
      authority_status: data.authority_status,
      // Only ever the backend's own answer. Defaulting to the requested org
      // would label a default-org address as the requested org's — the exact
      // silent staleness this field exists to detect.
      authority_org_id: data.authority_organisation_id
        ? String(data.authority_organisation_id)
        : '',
      name: data.name || data.full_name || `${data.first_name || ''} ${data.last_name || ''}`.trim(),
      first_name: data.first_name,
      last_name: data.last_name,
      full_name: data.full_name,
      organisation_id: data.organisation_id || data.org_id || '',
      organisation_name: data.organisation_name || data.org_name || '',
      role: derivedRole,
      permissions: data.permissions,
      avatar_url: data.avatar_url,
      phone: data.phone,
      aliases: Array.isArray(data.aliases) ? data.aliases : [],
    }
  } catch (err) {
    console.error('[auth] fetchUserProfile: exception', err)
    return null
  }
}

export type OrganisationSummary = { id: string; name: string }

/**
 * Decide which org id to trust as the active tenant. Pure I→O:
 * (cachedId, cachedName, orgs) -> { id, name }.
 *
 * WHY: the active org is materialised across several records (the
 * shared `.quotech.ai` `quotech_org` cookie, per-origin localStorage,
 * the Zustand store). They drift — a stale cookie left by a prior
 * session/tenant can point at an org the user isn't a member of. If we
 * trust it, the RBAC permissions call is mis-scoped (`?org_id=<stale>`)
 * and the backend returns an empty module set, which the HubGuard
 * renders as "Access Denied" — even for the org OWNER. Observed:
 * cfo.quotech.ai sent org_id=1 ("Quotech Demo") for the owner of org 4
 * ("Bravada Group"). Same family as BUG-0020/0021/0028.
 *
 * Rules:
 * - orgs unavailable (null/empty): keep the cached id — degrade safely
 *   on a network failure rather than logging the user out.
 * - cached id IS a real membership: keep it — multi-org safe, never
 *   override a deliberate org switch.
 * - cached id missing OR not a membership: fall back to the user's
 *   first/default org.
 */
export function resolveActiveOrg(
  cachedId: string | null,
  cachedName: string | null,
  orgs: OrganisationSummary[] | null | undefined,
): { id: string | null; name: string | null } {
  if (!orgs || orgs.length === 0) {
    return { id: cachedId, name: cachedName }
  }
  if (cachedId) {
    const match = orgs.find((o) => String(o.id) === String(cachedId))
    if (match) return { id: cachedId, name: match.name || cachedName }
  }
  return { id: orgs[0].id, name: orgs[0].name || null }
}

/** Fetch organisations the user belongs to (used to hydrate org name across subdomains) */
export async function fetchUserOrganisations(token: string): Promise<OrganisationSummary[] | null> {
  const url = `${API_BASE}/organisations/?page_size=150`
  console.log('[@quotech/auth] fetchUserOrganisations: calling', url)

  try {
    const response = await fetchWithTimeout(url, {
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    }, 8000)

    if (!response.ok) {
      const text = await response.text()
      console.warn('[@quotech/auth] fetchUserOrganisations: not ok', response.status, text)
      return null
    }

    const data = await response.json()
    const results = Array.isArray(data) ? data : (Array.isArray(data?.results) ? data.results : [])

    return results
      .map((o: Record<string, unknown>) => ({ id: String(o?.id || ''), name: String(o?.name || '').trim() }))
      .filter((o: OrganisationSummary) => !!o.id)
  } catch (err) {
    console.warn('[@quotech/auth] fetchUserOrganisations: exception', err)
    return null
  }
}

/** Refresh access token using refresh token */
export async function refreshAccessToken(refreshToken: string): Promise<TokenPair | null> {
  try {
    const response = await fetch(`${API_BASE}/auth/refresh-token/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh: refreshToken }),
    })
    if (!response.ok) return null
    const data = await response.json()
    return { access: data.access, refresh: data.refresh || refreshToken }
  } catch {
    return null
  }
}
